import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Tag, message, Tree, Popconfirm } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';

type MenuNode = { id: number; key: string; name: string; type: string; children?: MenuNode[] };

// 服务端菜单树 -> antd Tree 数据（key 用数字 id，与授权接口一致）
const toTreeData = (nodes: MenuNode[]): any[] =>
  nodes.map((n) => ({
    key: n.id,
    title: n.name,
    children: n.children ? toTreeData(n.children) : undefined,
  }));

const Roles: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [form] = Form.useForm();

  // 菜单授权弹窗
  const [assignFor, setAssignFor] = useState<any>(null);
  const [menuTreeData, setMenuTreeData] = useState<any[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<number[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/admin/role', { params: { page: p, pageSize: 10 } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const openCreate = () => {
    setEditItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: any) => {
    setEditItem(record);
    form.setFieldsValue({ name: record.name, code: record.code, remark: record.remark });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    if (saving) return;
    setSaving(true);
    try {
      if (editItem) {
        await request.put(`/admin/role/${editItem.id}`, { name: values.name, remark: values.remark });
      } else {
        await request.post('/admin/role', values);
      }
      message.success('保存成功');
      setModalOpen(false);
      form.resetFields();
      fetchList();
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/admin/role/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      message.success(record.status === 1 ? '已停用' : '已启用');
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (record: any) => {
    await request.delete(`/admin/role/${record.id}`);
    message.success('已删除');
    fetchList();
  };

  const openAssign = async (record: any) => {
    // 先重置上一角色的勾选态，避免残留值误写入本次角色
    setCheckedKeys([]);
    setMenuTreeData([]);
    setAssignFor(record);
    try {
      const [tree, menus] = (await Promise.all([
        request.get('/admin/menu/tree'),
        request.get(`/admin/role/${record.id}/menus`),
      ])) as [any, any];
      setMenuTreeData(toTreeData(tree || []));
      setCheckedKeys(Array.isArray(menus) ? menus : []);
    } catch {
      // 拉取失败不进入可提交态，防止空勾选覆盖该角色已有授权
      setAssignFor(null);
    }
  };

  const submitAssign = async () => {
    if (!assignFor || assignSaving) return;
    setAssignSaving(true);
    try {
      await request.put(`/admin/role/${assignFor.id}/menus`, { menuIds: checkedKeys });
      message.success('菜单授权已保存');
      setAssignFor(null);
      fetchList();
    } finally {
      setAssignSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '角色名称',
      dataIndex: 'name',
      render: (v: string, r: any) => (
        <Space>
          {v}
          {r.builtIn && <Tag color="red">内置</Tag>}
        </Space>
      ),
    },
    { title: '标识', dataIndex: 'code', width: 160 },
    { title: '备注', dataIndex: 'remark', ellipsis: true },
    { title: '账号数', dataIndex: 'adminCount', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v: number) => (v === 1 ? <Tag color="success">启用</Tag> : <Tag color="default">停用</Tag>),
    },
    {
      title: '操作',
      width: 240,
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Button size="small" disabled={record.builtIn} onClick={() => openAssign(record)}>
            分配菜单
          </Button>
          <Button
            size="small"
            disabled={record.builtIn}
            loading={busyId === record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '停用' : '启用'}
          </Button>
          {!record.builtIn && (
            <Popconfirm title="确认删除该角色？" onConfirm={() => handleDelete(record)} okText="删除" cancelText="取消">
              <Button size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>角色管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建角色
        </Button>
      </div>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Modal
        title={editItem ? '编辑角色' : '新建角色'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => !saving && setModalOpen(false)}
        okButtonProps={{ loading: saving }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="角色名称" rules={[{ required: true, message: '请输入角色名称' }]}>
            <Input placeholder="如：运营专员" />
          </Form.Item>
          <Form.Item
            name="code"
            label="角色标识"
            rules={[{ required: !editItem, message: '请输入角色标识' }]}
            extra={editItem ? '标识创建后不可修改' : '英文唯一标识，如 operator'}
          >
            <Input disabled={!!editItem} placeholder="如：operator" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`分配菜单（${assignFor?.name || ''}）`}
        open={!!assignFor}
        onOk={submitAssign}
        onCancel={() => !assignSaving && setAssignFor(null)}
        okButtonProps={{ loading: assignSaving, disabled: menuTreeData.length === 0 }}
        width={420}
      >
        <p style={{ color: '#6b7280', marginTop: 0 }}>勾选该角色可见的菜单；勾选分组会连带其下所有子菜单。</p>
        <Tree checkable treeData={menuTreeData} checkedKeys={checkedKeys}
          onCheck={(keys: any) => setCheckedKeys((Array.isArray(keys) ? keys : keys.checked) as number[])}
          defaultExpandAll selectable={false} />
      </Modal>
    </div>
  );
};

export default Roles;
